import Phaser from 'phaser';
import { LIGHTNING_WIZARD } from '../../config';
import { Enemy } from '../Enemy';
import { Projectile } from '../Projectile';
import { BuildingSlot, ChainUpgradeEffect, TargetPriority } from '../../types';
import { selectByPriority } from '../units/BaseUnit';
import { WALL_X, WALL_WIDTH } from '../../config';

export class LightningWizard {
    scene: Phaser.Scene;
    x: number;
    y: number;
    level: number;
    position: BuildingSlot;
    unitType: string = 'lightning_wizard';
    graphics: Phaser.GameObjects.Graphics;
    sprite: Phaser.GameObjects.Sprite;
    private attackTimer: number = 0;
    private projectiles: Projectile[];
    stackCount: number = 1;

    // Orb visual
    private orbGraphics: Phaser.GameObjects.Graphics;
    private orbPulse: number = 0;

    // Chain upgrade bonuses
    bonusDamageMult: number = 1;
    attackIntervalMult: number = 1;
    bonusChainCount: number = 0;
    globalDamageMult: number = 1;
    attackSpeedMult: number = 1;

    constructor(
        scene: Phaser.Scene,
        x: number,
        y: number,
        position: BuildingSlot,
        projectiles: Projectile[]
    ) {
        this.scene = scene;
        this.x = x;
        this.y = y;
        this.level = 1;
        this.position = position;
        this.graphics = scene.add.graphics();
        this.orbGraphics = scene.add.graphics().setDepth(100);
        this.sprite = scene.add.sprite(x, y, 'building_wizard');
        this.sprite.setOrigin(0.5, 110 / 128);
        this.projectiles = projectiles;
        this.stackCount = 1;
        this.draw();
    }

    getDamage(): number {
        return LIGHTNING_WIZARD.baseDamage * this.stackCount * this.bonusDamageMult * this.globalDamageMult;
    }

    getChainCount(): number {
        return LIGHTNING_WIZARD.chainCount + this.bonusChainCount;
    }

    addStack(): void {
        this.stackCount++;
    }

    applyChainUpgrade(effect: ChainUpgradeEffect): void {
        if (effect.damageMultBonus !== undefined) this.bonusDamageMult *= (1 + effect.damageMultBonus);
        if (effect.attackIntervalMult !== undefined) this.attackIntervalMult *= effect.attackIntervalMult;
        if (effect.extraProjectiles !== undefined) this.bonusChainCount += effect.extraProjectiles;
        this.level++;
        this.draw();
    }

    update(time: number, delta: number, enemies: Enemy[]): void {
        this.orbPulse += delta * 0.003;

        this.attackTimer -= delta;
        if (this.attackTimer <= 0) {
            const target = this.findTarget(enemies);
            if (target) {
                this.attackTimer = LIGHTNING_WIZARD.attackInterval * this.attackIntervalMult / this.attackSpeedMult;
                this.castLightning(target, enemies);
            }
        }
        this.draw();
        this.drawOrb();
    }

    private findTarget(enemies: Enemy[]): Enemy | null {
        const range = LIGHTNING_WIZARD.range;
        const wallEdge = WALL_X + WALL_WIDTH;
        const candidates: Enemy[] = [];
        for (const enemy of enemies) {
            if (enemy.isDead) continue;
            if (enemy.enemyType === 'boulder') continue;
            if (enemy.isBurrowed) continue;
            if (enemy.isGoblin) continue;
            if (enemy.x - wallEdge > range) continue;
            candidates.push(enemy);
        }
        return selectByPriority(candidates, 'highestHP' as TargetPriority, LIGHTNING_WIZARD.orbX, LIGHTNING_WIZARD.orbY);
    }

    private castLightning(target: Enemy, allEnemies: Enemy[]): void {
        const damage = this.getDamage();
        const chains = this.getChainCount();

        // Primary strike from orb to target
        const killed = target.takeDamage(damage);
        if (killed) {
            // processEnemyKill is handled by GameScene checking isDead
        }

        // Visual: lightning bolt from orb to target
        const gfx = this.scene.add.graphics().setDepth(200);
        this.drawLightningBolt(gfx, LIGHTNING_WIZARD.orbX, LIGHTNING_WIZARD.orbY, target.x, target.y, 0xffff00, 3);

        // Chain to nearby enemies
        let currentTarget = target;
        let chainDamage = damage;
        for (let c = 0; c < chains; c++) {
            chainDamage = Math.round(chainDamage * LIGHTNING_WIZARD.chainDamageDecay);
            let nearest: Enemy | null = null;
            let minDist = 150;

            for (const enemy of allEnemies) {
                if (enemy.isDead || enemy === currentTarget) continue;
                if (enemy.isBurrowed || enemy.isGoblin) continue;
                const dx = enemy.x - currentTarget.x;
                const dy = enemy.y - currentTarget.y;
                const dist = Math.sqrt(dx * dx + dy * dy);
                if (dist < minDist) {
                    minDist = dist;
                    nearest = enemy;
                }
            }

            if (!nearest) break;

            nearest.takeDamage(chainDamage);
            this.drawLightningBolt(gfx, currentTarget.x, currentTarget.y, nearest.x, nearest.y, 0x88ccff, 2);
            currentTarget = nearest;
        }

        // Fade out lightning visual
        this.scene.tweens.add({
            targets: gfx,
            alpha: 0,
            duration: 300,
            onComplete: () => gfx.destroy(),
        });
    }

    private drawLightningBolt(
        gfx: Phaser.GameObjects.Graphics,
        x1: number, y1: number,
        x2: number, y2: number,
        color: number, width: number
    ): void {
        gfx.lineStyle(width, color, 1);
        gfx.beginPath();
        gfx.moveTo(x1, y1);

        const dx = x2 - x1;
        const dy = y2 - y1;
        const segments = 5;
        for (let i = 1; i < segments; i++) {
            const t = i / segments;
            const jitterX = (Math.random() - 0.5) * 20;
            const jitterY = (Math.random() - 0.5) * 20;
            gfx.lineTo(x1 + dx * t + jitterX, y1 + dy * t + jitterY);
        }
        gfx.lineTo(x2, y2);
        gfx.strokePath();
    }

    private drawOrb(): void {
        this.orbGraphics.clear();
        const ox = LIGHTNING_WIZARD.orbX;
        const oy = LIGHTNING_WIZARD.orbY;
        const pulse = Math.sin(this.orbPulse) * 0.3 + 0.7;

        // Outer glow
        this.orbGraphics.fillStyle(0x4488ff, 0.15 * pulse);
        this.orbGraphics.fillCircle(ox, oy, 18);

        // Inner orb
        this.orbGraphics.fillStyle(0x88ccff, 0.6 * pulse);
        this.orbGraphics.fillCircle(ox, oy, 10);

        // Core
        this.orbGraphics.fillStyle(0xffffff, 0.8);
        this.orbGraphics.fillCircle(ox, oy, 4);

        // Crackling arcs
        this.orbGraphics.lineStyle(1, 0xffff88, 0.5 * pulse);
        for (let i = 0; i < 3; i++) {
            const angle = this.orbPulse * 2 + (i / 3) * Math.PI * 2;
            const ex = ox + Math.cos(angle) * 14;
            const ey = oy + Math.sin(angle) * 14;
            this.orbGraphics.beginPath();
            this.orbGraphics.moveTo(ox + Math.cos(angle) * 6, oy + Math.sin(angle) * 6);
            this.orbGraphics.lineTo(ex, ey);
            this.orbGraphics.strokePath();
        }

        // Connection line from wizard to orb
        this.orbGraphics.lineStyle(1, 0x6688cc, 0.3);
        this.orbGraphics.beginPath();
        this.orbGraphics.moveTo(this.x, this.y - 20);
        this.orbGraphics.lineTo(ox, oy);
        this.orbGraphics.strokePath();
    }

    draw(): void {
        this.sprite.setTexture('building_wizard');
        this.graphics.clear();

        if (this.level > 1) {
            this.graphics.fillStyle(0xffff44, 1);
            for (let i = 0; i < Math.min(this.level - 1, 5); i++) {
                this.graphics.fillCircle(this.x - 6 + i * 3, this.y + 8, 1.5);
            }
        }
    }

    destroy(): void {
        this.graphics.destroy();
        this.sprite.destroy();
        this.orbGraphics.destroy();
    }
}
